import React, { useState, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom search dropdown component (same as before)
const SearchableDropdown = ({ options, value, onChange, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-dropdown" style={{ position: 'relative' }}>
      <label>{label}:</label>
      <div>
        <input
          type="text"
          value={isOpen ? searchTerm : (value || placeholder)}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={placeholder}
          style={{
            width: '200px',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option}
                  onMouseDown={() => handleSelect(option)}
                  style={{
                    padding: '8px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {option}
                </div>
              ))
            ) : (
              <div style={{ padding: '8px', color: '#666' }}>No matches found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DataVisualization = ({ predictionData, historicalData, selectedVariety, selectedProductionArea, selectedSeason,
                             setSelectedVariety, setSelectedProductionArea, setSelectedSeason }) => {
  // State for toast notification
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  // Get unique values for filters
  const varieties = useMemo(() => {
    const varietySet = new Set();
    predictionData.forEach(item => varietySet.add(item.Variety));
    historicalData.forEach(item => varietySet.add(item.Variety));
    return Array.from(varietySet).sort();
  }, [predictionData, historicalData]);

  const productionAreas = useMemo(() => {
    const areaSet = new Set();
    historicalData.forEach(item => areaSet.add(item.ProductionArea));
    return Array.from(areaSet).sort();
  }, [historicalData]);

  const seasons = useMemo(() => {
    const seasonSet = new Set();
    historicalData.forEach(item => {
      if (item.Season) seasonSet.add(item.Season);
    });
    return Array.from(seasonSet).sort();
  }, [historicalData]);

  // Season shapes mapping (colors will be handled by data type)
  const seasonStyles = {
    '1': { shape: 'circle' },
    'Jan': { shape: 'triangle' },
    'Jun': { shape: 'square' },
    'Nov': { shape: 'diamond' },
    'fall': { shape: 'hexagon' },
    'spring': { shape: 'star' },
    'summer': { shape: 'cross' },
    'Predicted': { shape: 'diamond' },
    'default': { shape: 'circle' }
  };

  // Data type colors mapping
  const dataTypeColors = {
    'historical_yield': '#8884d8',
    'historical_clean_yield': '#82ca9d',
    'predicted_yield': '#e34409',
    'predicted_clean_yield': '#920ec6'
  };

  // Helper function to safely parse numeric values and round to 2 decimal places
  const safeParseFloat = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : Math.round(parsed * 100) / 100;  // Round to 2 decimal places
  };

  // Filter and process data for visualization
  const { scatterData, xAxisDomain } = useMemo(() => {
    if (!selectedVariety) return { scatterData: [], xAxisDomain: [0, 0] };

    console.log('Processing data for variety:', selectedVariety);

    // Filter historical data using selectedProductionArea and selectedSeason
    let filteredHistorical = historicalData.filter(item =>
      item.Variety === selectedVariety &&
      (selectedProductionArea === 'all' || item.ProductionArea === selectedProductionArea) &&
      (selectedSeason === 'all' || item.Season === selectedSeason)
    );

    // Create scatter points for historical data
    const historicalPoints = [];
    filteredHistorical.forEach(item => {
      const year = parseInt(item.Year);
      const yieldValue = safeParseFloat(item.yield_g_m2_week);
      const cleanYieldValue = safeParseFloat(item.clean_yield_g_m2_week);
      const fpoValue = item.FPO; // Access the FPO value here

      if (yieldValue !== null && yieldValue > 0) {
        historicalPoints.push({
          x: year,
          y: yieldValue,
          FPO: fpoValue,
          type: 'historical_yield',
          season: item.Season || 'Unknown',
          dataType: 'Historical Yield'
        });
      }

      if (cleanYieldValue !== null && cleanYieldValue > 0) {
        historicalPoints.push({
          x: year,
          y: cleanYieldValue,
          FPO: fpoValue,
          type: 'historical_clean_yield',
          season: item.Season || 'Unknown',
          dataType: 'Historical Clean Yield'
        });
      }
    });

    // Add prediction data
    const prediction = predictionData.find(item =>
      item.Variety === selectedVariety &&
      (selectedProductionArea === 'all' || item.ProductionArea === selectedProductionArea) &&
      (selectedSeason === 'all' || item.Season === selectedSeason)
    );

    const currentYear = new Date().getFullYear();
    const predictionPoints = [];

    if (prediction) {
      console.log('Found prediction:', prediction);

      const predictedYield = safeParseFloat(prediction.Predicted_base_rate_g_m2_week);
      const predictedCleanYield = safeParseFloat(prediction.Clean_predicted_base_rate_g_m2_week);

      if (predictedYield !== null && predictedYield > 0) {
        predictionPoints.push({
          x: currentYear,
          y: predictedYield,
          type: 'predicted_yield',
          season: 'Predicted',
          dataType: 'Predicted Yield'
        });
      }

      if (predictedCleanYield !== null && predictedCleanYield > 0) {
        predictionPoints.push({
          x: currentYear,
          y: predictedCleanYield,
          type: 'predicted_clean_yield',
          season: 'Predicted',
          dataType: 'Predicted Clean Yield'
        });
      }
    }

    const allPoints = [...historicalPoints, ...predictionPoints];

    // Calculate x-axis domain with extra year padding
    if (allPoints.length === 0) {
      return { scatterData: [], xAxisDomain: [0, 0] };
    }

    let minYear = Math.min(...allPoints.map(p => p.x));
    let maxYear = Math.max(...allPoints.map(p => p.x));

    // Extend by one year on both sides for better visualization
    const xDomain = [minYear - 1, maxYear + 1];

    return {
      scatterData: allPoints,
      xAxisDomain: xDomain
    };
  }, [selectedVariety, selectedProductionArea, selectedSeason, historicalData, predictionData]);

  // Handle point click to copy FPO value
  const handlePointClick = (data) => {
    const fpoValue = data.payload.FPO; // Access the FPO value from the clicked point
    if (fpoValue) {
      navigator.clipboard.writeText(fpoValue.toString())
        .then(() => {
          setToastMessage(`Copied FPO value: ${fpoValue}`);
          setToastVisible(true);
          setTimeout(() => setToastVisible(false), 3000); // Hide after 3 seconds
        })
        .catch(err => {
          setToastMessage('Failed to copy value.');
          setToastVisible(true);
          setTimeout(() => setToastVisible(false), 3000); // Hide after 3 seconds
          console.error('Failed to copy: ', err);
        });
    } else {
      setToastMessage('No FPO value available to copy.');
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 3000); // Hide after 3 seconds
    }
  };

  // Group data by type for separate scatter series
  const groupedData = useMemo(() => {
    const groups = {
      historical_yield: [],
      historical_clean_yield: [],
      predicted_yield: [],
      predicted_clean_yield: []
    };

    scatterData.forEach(point => {
      if (groups[point.type]) {
        groups[point.type].push(point);
      }
    });

    return groups;
  }, [scatterData]);

  // Custom dot component for different seasons and data types
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;

    // Check if coordinates are valid numbers
    if (!payload || cx === null || cy === null || isNaN(cx) || isNaN(cy)) {
      return null;
    }

    const style = seasonStyles[payload.season] || seasonStyles.default;
    const color = dataTypeColors[payload.type] || '#8884d8';
    const size = payload.type && payload.type.includes('predicted') ? 8 : 6;

    if (style.shape === 'triangle') {
      return (
        <polygon
          points={`${cx},${cy-size} ${cx-size},${cy+size} ${cx+size},${cy+size}`}
          fill={color}
          stroke={color}
        />
      );
    } else if (style.shape === 'square') {
      return (
        <rect
          x={cx - size/2}
          y={cy - size/2}
          width={size}
          height={size}
          fill={color}
          stroke={color}
        />
      );
    } else if (style.shape === 'diamond') {
      return (
        <polygon
          points={`${cx},${cy-size} ${cx+size},${cy} ${cx},${cy+size} ${cx-size},${cy}`}
          fill={color}
          stroke={color}
        />
      );
    } else if (style.shape === 'hexagon') {
      const h = size * 0.866; // height factor for hexagon
      return (
        <polygon
          points={`${cx-size},${cy} ${cx-size/2},${cy-h} ${cx+size/2},${cy-h} ${cx+size},${cy} ${cx+size/2},${cy+h} ${cx-size/2},${cy+h}`}
          fill={color}
          stroke={color}
        />
      );
    } else if (style.shape === 'star') {
      const outer = size;
      const inner = size * 0.4;
      const points = [];
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? outer : inner;
        const angle = (i * Math.PI) / 5 - Math.PI / 2;
        points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`);
      }
      return (
        <polygon
          points={points.join(' ')}
          fill={color}
          stroke={color}
        />
      );
    } else if (style.shape === 'cross') {
      const thickness = size / 3;
      return (
        <g>
          <rect x={cx - thickness/2} y={cy - size} width={thickness} height={size * 2} fill={color} />
          <rect x={cx - size} y={cy - thickness/2} width={size * 2} height={thickness} fill={color} />
        </g>
      );
    } else {
      // Default circle
      return (
        <circle
          cx={cx}
          cy={cy}
          r={size}
          fill={color}
          stroke={color}
        />
      );
    }
  };

  return (
    <div>
      <div className="filters" style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <SearchableDropdown
          options={varieties}
          value={selectedVariety}
          onChange={setSelectedVariety} // Update variety
          placeholder="Select a variety"
          label="Variety"
        />

        <div className="filter-group">
          <label htmlFor="area-select">Production Area:</label>
          <select
            id="area-select"
            value={selectedProductionArea}
            onChange={(e) => setSelectedProductionArea(e.target.value)} // Update production area
            style={{ padding: '8px', marginLeft: '5px' }}
          >
            <option value="all">All Areas</option>
            {productionAreas.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="season-select">Season:</label>
          <select
            id="season-select"
            value={selectedSeason}
            onChange={(e) => {
              setSelectedSeason(e.target.value);
            }}
            style={{ padding: '8px', marginLeft: '5px' }}
          >
            <option value="all">All Seasons</option>
            {seasons.map(season => (
              <option key={season} value={season}>{season}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Season legend */}
      {selectedVariety && scatterData.length > 0 && (
        <div style={{ marginBottom: '10px', fontSize: '12px' }}>
          <div style={{ marginBottom: '5px' }}>
            <strong>Data Type Colors:</strong>
            <span style={{ marginLeft: '15px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#8884d8', marginRight: '5px' }}></span>
              Historical Yield
            </span>
            <span style={{ marginLeft: '15px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#82ca9d', marginRight: '5px' }}></span>
              Historical Clean Yield
            </span>
            <span style={{ marginLeft: '15px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#e34409', marginRight: '5px' }}></span>
              Predicted Yield
            </span>
            <span style={{ marginLeft: '15px' }}>
              <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#920ec6', marginRight: '5px' }}></span>
              Predicted Clean Yield
            </span>
          </div>

          <div>
            <strong>Season Shapes:</strong>
            {seasons.map(season => {
              const style = seasonStyles[season] || seasonStyles.default;
              const size = 8;

              let shapeIcon;
              if (style.shape === 'triangle') {
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <polygon points={`8,${8-size} ${8-size},${8+size} ${8+size},${8+size}`} fill="#666" />
                  </svg>
                );
              } else if (style.shape === 'square') {
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <rect x={8-size/2} y={8-size/2} width={size} height={size} fill="#666" />
                  </svg>
                );
              } else if (style.shape === 'diamond') {
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <polygon points={`8,${8-size} ${8+size},8 8,${8+size} ${8-size},8`} fill="#666" />
                  </svg>
                );
              } else if (style.shape === 'hexagon') {
                const h = size * 0.866;
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <polygon points={`${8-size},8 ${8-size/2},${8-h} ${8+size/2},${8-h} ${8+size},8 ${8+size/2},${8+h} ${8-size/2},${8+h}`} fill="#666" />
                  </svg>
                );
              } else if (style.shape === 'star') {
                const outer = size;
                const inner = size * 0.4;
                const points = [];
                for (let i = 0; i < 10; i++) {
                  const radius = i % 2 === 0 ? outer : inner;
                  const angle = (i * Math.PI) / 5 - Math.PI / 2;
                  points.push(`${8 + radius * Math.cos(angle)},${8 + radius * Math.sin(angle)}`);
                }
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <polygon points={points.join(' ')} fill="#666" />
                  </svg>
                );
              } else if (style.shape === 'cross') {
                const thickness = size / 3;
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <rect x={8 - thickness/2} y={8 - size} width={thickness} height={size * 2} fill="#666" />
                    <rect x={8 - size} y={8 - thickness/2} width={size * 2} height={thickness} fill="#666" />
                  </svg>
                );
              } else {
                // Default circle
                shapeIcon = (
                  <svg width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
                    <circle cx="8" cy="8" r={size} fill="#666" />
                  </svg>
                );
              }

              return (
                <span key={season} style={{ marginLeft: '15px' }}>
                  {shapeIcon}
                  {season}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {selectedVariety && scatterData.length > 0 ? (
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"  // Changed from "year" to "x"
                type="number"
                domain={xAxisDomain}
                tickCount={Math.min(10, xAxisDomain[1] - xAxisDomain[0] + 1)}
              />
              <YAxis
                dataKey="y"  // Added dataKey for y-axis
                label={{ value: 'Yield (g/m²/week)', angle: -90, position: 'insideLeft' }}
                domain={[(dataMin) => Math.max(0, dataMin - 0.5), 'dataMax + 0.5']}
                tickFormatter={(value) => value.toFixed(2)}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length > 0) {
                    const year = payload[0].payload.x; // Access the x value (year) from the first item in payload
                    const uniquePayloads = payload.filter((item, index, self) =>
                      index === self.findIndex(p => p.payload.type === item.payload.type)
                    );

                    return (
                      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <p>{`Year: ${year}`}</p> {/* Display the year */}
                        {uniquePayloads.map((item, index) => (
                          <p key={index} style={{ color: item.color }}>
                            {`${item.payload.dataType} (${item.payload.season}): ${item.payload.y.toFixed(2)} g/m²/week`} {/* Access y value here */}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />

              <Scatter
                name="Historical Yield"
                data={groupedData.historical_yield}
                fill="#8884d8"
                shape={<CustomDot />}
                onClick={handlePointClick}
              />
              <Scatter
                name="Historical Clean Yield"
                data={groupedData.historical_clean_yield}
                fill="#82ca9d"
                shape={<CustomDot />}
                onClick={handlePointClick}
              />
              <Scatter
                name="Predicted Yield"
                data={groupedData.predicted_yield}
                fill="#e34409"
                shape={<CustomDot />}
                onClick={handlePointClick}
              />
              <Scatter
                name="Predicted Clean Yield"
                data={groupedData.predicted_clean_yield}
                fill="#920ec6"
                shape={<CustomDot />}
                onClick={handlePointClick}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : selectedVariety ? (
        <div className="no-data-message">
          <p>No data available for the selected filters.</p>
        </div>
      ) : (
        <div className="select-variety-message">
          <p>Please select a variety to view the yield visualization.</p>
        </div>
      )}

      {toastVisible && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          zIndex: 1000
        }}>
          {toastMessage}
        </div>
      )}

    </div>
  );
};

export default DataVisualization;